// Application roles mirror the backend authority model (DECISIONS D-003/D-010 and the
// SECURITY-DEFINER *_authorize RPCs). The UI never grants authority — RLS remains the
// source of truth; these types only drive role-aware navigation and route guards.
// Roles mirror the backend `roles.code` check constraint (migration 0003). Authority is
// enforced by RLS + the *_authorize RPCs; these drive UI navigation/guards only.
export const APP_ROLES = [
  'administrator',
  'finance_manager',
  'finance_operator',
  'management',
  'sales_manager',
] as const;

export type AppRole = (typeof APP_ROLES)[number];

// Georgian labels for the role codes. `Record<AppRole, string>` on purpose: adding a role to
// APP_ROLES without a label is a compile error, not an untranslated code leaking into the UI.
export const ROLE_LABELS: Record<AppRole, string> = {
  administrator: 'ადმინისტრატორი',
  finance_manager: 'ფინანსური მენეჯერი',
  finance_operator: 'ფინანსური ოპერატორი',
  management: 'მენეჯმენტი',
  sales_manager: 'გაყიდვების მენეჯერი',
};

/** Roles in the fixed APP_ROLES order, so the same set always reads identically. */
export function roleLabels(roles: readonly AppRole[]): string[] {
  return APP_ROLES.filter((role) => roles.includes(role)).map((role) => ROLE_LABELS[role]);
}

// Roles the reporting layer treats as company-wide scope (0020 fn_report_authorize).
export const COMPANY_SCOPE_ROLES: readonly AppRole[] = [
  'administrator',
  'finance_manager',
  'finance_operator',
  'management',
];

export function isAppRole(value: unknown): value is AppRole {
  return typeof value === 'string' && (APP_ROLES as readonly string[]).includes(value);
}

// The authenticated Supabase session (auth layer only).
export interface AuthUser {
  readonly authUserId: string;
  readonly email: string | null;
}
